import { DISPATCH_STATUS_CODE } from '@/constants';
import { container } from '@/container';
import CONSTANTS from '@/helpers/constants';
import { Logger } from '@/services/Logger';
import { TripUpdateReq } from '@/services/signalr/MessageTypes/TripUpdateReq';
import { Signalr } from '@/services/signalr/Signalr';
import { TripStore } from '@/stores/TripStore';

export interface UnassignTripParams {
  tripId: string;
  resourceName?: string;
  status: string | number;
  unassignId?: string;
  unassignReason?: string;
  /** FE-only flag echoed back via StatusMsg context so the toast can say "inactive shift" */
  isInactiveShift?: boolean;
}

/**
 * Unassign a unit from a trip via SignalR.
 * @param params - The unassignment parameters
 */
export async function unassignTrip(params: UnassignTripParams): Promise<void> {
  try {
    const signalr = await container.getAsync(Signalr);
    const resourceName = params.resourceName || container.get(TripStore).find(params.tripId)?.unit?.name || '';

    // The unassign COMMAND code is `status * -1 + -10`, and the BE only accepts the
    // codes derived from real dispatch statuses (1..7 → -11..-17).
    //
    // Pre-assigned trips carry status '8', which would produce -18. That is NOT a
    // recognised command: sending -18 was verified to be a silent no-op (the trip came
    // back unchanged, still tripStatus 6 / status 8). So map '8' → 1 to send -11.
    //
    // Do not confuse this with the response: after a successful unassign the BE records
    // status.id as -18 for a previously pre-assigned trip, encoding "unassigned from
    // Pre-Assigned". -18 is a valid *result*, never a valid *command*.
    //
    // Also clamp to minimum 1 for edge cases where status is '0' (Open), on the same
    // reasoning — -10 is unlikely to be a recognised command either.
    // Number.isFinite guard: a missing or non-numeric status would otherwise produce
    // NaN here and send status.id "NaN" to the BE.
    const numericStatus = Number(params.status);
    const effectiveStatus = (
      !Number.isFinite(numericStatus) ||
      numericStatus === Number(DISPATCH_STATUS_CODE.PRE_ASSIGNED)
    )
      ? 1
      : Math.max(numericStatus, 1);

    await signalr.invoke(TripUpdateReq, {
      contextKey: CONSTANTS.UI.CONTEXTS.UNASSIGN_TRIP,
      id: params.tripId,
      ...(resourceName ? { resourceName } : {}),
      ...(params.isInactiveShift ? { isInactiveShift: true } : {}),
      status: { id: `${effectiveStatus * -1 + -10}` },
      tripReasons: params.unassignId
        ? [
          {
            reasonType: '1',
            reasonValue: { id: params.unassignId, value: params.unassignReason },
          },
        ]
        : null,
    });
  }
  catch (error) {
    const logger = container.get(Logger);
    logger.captureException(error as Error, { tags: { updateTrip: 'unassignTrip' } });
    throw error;
  }
}
